import React, { useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "organisms/iconCommonent";
import { FaRegEdit } from "react-icons/fa";

interface BannerVideo {
  _id: string;
  heading: string;
  description?: string;
  image: string;
  localVideo?: string;
  video?: string;
}

export default function Banner() {
  const [banner, setBanner] = useState<BannerVideo | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerVideo | null>(null);

  // Fetch banner data
  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_LOCAL_URL}/api/social-changes`);
      const selectedBanner = res.data.message[0];
      setBanner(selectedBanner);
    } catch (error) {
      console.error("Error fetching banner:", error);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleEdit = (banner: BannerVideo) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  return (
    <div className="pt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-lato-regular">Section - 01</h2>
        <hr />
      </div>

      {banner && (
        <div className="w-full bg-white border border-gray-200 rounded-xl overflow-hidden">
          {/* Show only one video (YouTube > local) */}
          {banner.video ? (
            <iframe
              width="100%"
              height="400"
              src={banner.video}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="mt-4"
            ></iframe>
          ) : banner.localVideo ? (
            <video
              src={`${process.env.REACT_APP_LOCAL_URL}/${banner.localVideo}`}
              autoPlay
              loop
              muted
              controls
              className="w-full h-[24rem] mt-4 object-cover"
            />
          ) : null}

          <div className="p-5">
            <h2
              className="text-xl font-semibold text-black font-lato-regular mb-2"
              dangerouslySetInnerHTML={{ __html: banner.heading }}
            />
            <div className="flex justify-start gap-3">
              <button
                className="bg-blue-500 font-lato-regular text-white px-4 py-2 rounded"
                onClick={() => handleEdit(banner)}
              >
                <span className="inline-block mr-1">
                  <Icon icon={FaRegEdit} />
                </span>
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && editingBanner && (
        <BannerFormModal
          onClose={() => setShowForm(false)}
          fetchBanners={fetchBanners}
          editingBanner={editingBanner}
        />
      )}
    </div>
  );
}

// ------------------- Modal Form -------------------
interface BannerFormModalProps {
  onClose: () => void;
  fetchBanners: () => void;
  editingBanner: BannerVideo;
}

const BannerFormModal = ({
  onClose,
  fetchBanners,
  editingBanner,
}: BannerFormModalProps) => {
  const [heading, setHeading] = useState(editingBanner.heading || "");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [youtubeVideo, setYoutubeVideo] = useState<string>(editingBanner.video || "");
  const [existingLocalVideo, setExistingLocalVideo] = useState<string>(
    editingBanner.localVideo || ""
  );

  // When user types a YouTube link → remove local video preview
  const handleYoutubeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setYoutubeVideo(value);
    if (value.trim() !== "") {
      setVideoFile(null);
      setExistingLocalVideo(""); // hide local preview
    }
  };

  // When user uploads local video → remove YouTube preview
  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setVideoFile(file);
    if (file) {
      setYoutubeVideo(""); // clear YouTube field
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!heading.trim()) {
      alert("Heading is required");
      return;
    }

    if (!youtubeVideo && !videoFile && !existingLocalVideo) {
      alert("Please add either a YouTube link or a local video.");
      return;
    }

    const formData = new FormData();
    formData.append("heading", heading);

    if (youtubeVideo) {
      formData.append("video", youtubeVideo);
      formData.append("localVideo", ""); // clear local
    } else if (videoFile) {
      formData.append("localVideo", videoFile);
      formData.append("video", ""); // clear YouTube
    } else if (existingLocalVideo) {
      // keep old local video if user didn’t change anything
      formData.append("localVideo", existingLocalVideo);
      formData.append("video", "");
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_LOCAL_URL}/api/social-changes/${editingBanner._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      fetchBanners();
      onClose();
    } catch (error) {
      console.error("Error updating banner:", error);
      alert("Failed to update video. Check console for details.");
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black/40 z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg overflow-y-auto max-h-[90vh]">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Heading */}
          <label className="block font-lato-bold">Heading</label>
          <input
            type="text"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            required
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {/* YouTube Link */}
          <label className="block font-lato-bold">YouTube Video Link</label>
          <input
            type="text"
            value={youtubeVideo}
            onChange={handleYoutubeChange}
            placeholder="Enter YouTube video link (optional)"
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {/* Local Video Upload */}
          <label className="block font-lato-bold">Upload Local Video</label>
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {/* Preview */}
          <div className="space-y-3 mt-2">
            {videoFile ? (
              <video
                src={URL.createObjectURL(videoFile)}
                controls
                muted
                loop
                className="w-full h-40 object-cover rounded-md"
              />
            ) : youtubeVideo ? (
              <iframe
                width="100%"
                height="200"
                src={youtubeVideo}
                title="YouTube preview"
                allowFullScreen
                className="rounded-md"
              ></iframe>
            ) : existingLocalVideo ? (
              <video
                src={`${process.env.REACT_APP_LOCAL_URL}/${existingLocalVideo}`}
                controls
                muted
                loop
                className="w-full h-40 object-cover rounded-md"
              />
            ) : null}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Update
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
